import QueryString from 'qs';
import { cleanupRequest } from '../API/cleanupRequests';

export const convertDataToString = (data: any): string => {
  const cleanedData = cleanupRequest(data);
  return QueryString.stringify(cleanedData, { arrayFormat: 'indices', format: 'RFC3986' });
};

export const convertLocationSearchToDataStringParams = (search: string, arrayLimit?: number): QueryString.ParsedQs => {
  if (arrayLimit) {
    return QueryString.parse(search, { ignoreQueryPrefix: true, arrayLimit });
  }
  return QueryString.parse(search, { ignoreQueryPrefix: true });
};

export interface ParsedQsConversionOptions {
  skipConversion?: boolean;
  convertNumbers?: boolean;
  convertPagination?: boolean;
  convertBooleans?: boolean;
}
/*
 * the qs library, as a policy, does not cast strings like "false" or "10" to literal values like the boolean false or the number 10.
 * https://github.com/ljharb/qs/issues/91
 * This conversion function is blunt. There are some options to turn it on and off.
 * It does not "dig down" and recursively convert nested objects or arrays.
 * The default option is to convert only pagination.
 * Future extensions might consider:
 *   - digging down
 *   - a better options system
 *   - looking at putting this in the options object of QueryString.parse as the `decode` option
 */
export const convertParsedQsToLiterals = (
  parsedQs: QueryString.ParsedQs = {},
  options: ParsedQsConversionOptions = {
    skipConversion: false,
    convertNumbers: false,
    convertPagination: true,
    convertBooleans: false,
  }
): { [key: string]: QueryString.ParsedQs | number | boolean | null } | QueryString.ParsedQs => {
  if (options.skipConversion) {
    return parsedQs;
  }
  const valuesWithLiterals = {};
  Object.entries(parsedQs).forEach(([queryStringKey, queryStringValue]) => {
    if (typeof queryStringValue === 'string') {
      if (options.convertBooleans && mapBooleans.has(queryStringValue)) {
        valuesWithLiterals[queryStringKey] = mapBooleans.get(queryStringValue);
        return;
      }
      if (options.convertPagination && paginationKeys.has(queryStringKey)) {
        const parsedInt = parseInt(queryStringValue);
        if (!Number.isNaN(parsedInt)) {
          valuesWithLiterals[queryStringKey] = parsedInt;
          return;
        }
      }
      if (options.convertNumbers && !paginationKeys.has(queryStringKey)) {
        const parsedFloat = parseFloat(queryStringValue);
        if (!Number.isNaN(parsedFloat)) {
          valuesWithLiterals[queryStringKey] = parsedFloat;
          return;
        }
      }
    }
    valuesWithLiterals[queryStringKey] = queryStringValue;
  });
  return valuesWithLiterals;
};

const paginationKeys = new Set<string>(['pageNumber', 'pageSize']);

const mapBooleans = new Map<string, boolean>([
  ['true', true],
  ['false', false],
]);
